import React, { Fragment, useEffect, useState } from 'react';
import Page, { usePage } from '../../components/Page';
import { Button, Grid, TextField } from '@mui/material';
import { SideBar, useSideBar } from '../../components/SideBar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { api } from '../../api';
import { faBars, faMagnifyingGlass, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { useStyles } from '../../hooks/useStyles';
import { DataGrid, GridColDef, GridColumnHeaderParams, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import { EditCarDialog, useEditCarDialog } from './EditCarDialog';
import { useAuth } from '../../units/auth/AuthProvider';
import { Car } from '../../api/car';
import { EditUserDialog, useEditUserDialog } from './EditUserDialog';
import { UserGroup } from '../../api/user-group';
import { grey } from '@mui/material/colors';

export const useCarManagePage = () => {
    const page = usePage();
    const classes = useStyles();
    const auth = useAuth();
    const [mode, setMode] = useState<'create' | 'update'>('create');
    const [cars, setCars] = useState<Car[]>([]);
    const reloadCars = () =>
        api.car
            .get({ relations: { users: true } })
            .then((ret) => ret.success && setCars(ret.data?.sort((a, b) => a.carId!.localeCompare(b.carId!)) || []));
    const sideBar = useSideBar();
    const editCarDialog = useEditCarDialog({ ...page, reloadCars });
    const editUserDialog = useEditUserDialog();
    const [inputSearchConfig, setInputSearchConfig] = useState('');
    const columns: GridColDef[] = [
        {
            field: 'carId',
            headerName: '車牌號碼',
            flex: 1,
            minWidth: 80,
            cellClassName: 'cell',
            renderCell: (params: GridRenderCellParams) => {
                const row = params.row as Car;
                return <>{row.carId}</>;
            },
            hide: false,
        },
        {
            field: 'users',
            headerName: '使用者',
            flex: 2,
            minWidth: 180,
            cellClassName: 'cell',
            renderCell: (params: GridRenderCellParams) => {
                const row = params.row as Car;
                return (
                    <Fragment>
                        {(row.users || []).map((user) => (
                            <Button
                                variant='contained'
                                className={classes.buttonWhite}
                                style={{ textTransform: 'none' }}
                                onClick={() => {
                                    editUserDialog.setUserId(user.userId || '');
                                    editUserDialog.setOnUpdateUser(
                                        () =>
                                            async (
                                                userId: string,
                                                inputAccount: string,
                                                inputPassword: string,
                                                inputUserName: string,
                                                inputTel: string,
                                                inputMobile: string,
                                                inputEmail: string,
                                                inputAddress: string,
                                                inputUserGroups: UserGroup[],
                                                inputCarIds: string[],
                                            ) => {
                                                await api.user
                                                    .update({
                                                        userId: userId,
                                                        account: inputAccount,
                                                        password: inputPassword || undefined,
                                                        userName: inputUserName,
                                                        tel: inputTel || null,
                                                        mobile: inputMobile || null,
                                                        email: inputEmail || null,
                                                        address: inputAddress || null,
                                                        userGroups: inputUserGroups,
                                                        cars: inputCarIds.map((carId) => ({ carId } as Car)),
                                                    })
                                                    .then((ret) => {
                                                        if (ret.success) {
                                                            reloadCars && reloadCars();
                                                            editUserDialog.onClose();
                                                            page.simpleAlert.show({
                                                                autoHideDuration: 2000,
                                                                severity: 'success',
                                                                message: ret.message,
                                                            });
                                                        } else {
                                                            page.simpleAlert.show({
                                                                autoHideDuration: 6000,
                                                                severity: 'error',
                                                                message: JSON.stringify(ret),
                                                            });
                                                        }
                                                    });
                                            },
                                    );
                                    editUserDialog.onOpen();
                                }}
                            >
                                {user.userName || user.account!}
                            </Button>
                        ))}
                    </Fragment>
                );
            },
            hide: false,
        },
        {
            field: 'action',
            renderHeader: (params: GridColumnHeaderParams) => <>操作</>,
            width: 180,
            cellClassName: 'cell',
            renderCell: (params: GridRenderCellParams) => {
                const row = params.row as Car;
                return (
                    <Grid container justifyContent={'space-around'}>
                        <Button
                            variant='contained'
                            className={classes.buttonWhite}
                            onClick={() => {
                                editCarDialog.setInputCarId(row.carId || '');
                                editCarDialog.setEditMode('update');
                                editCarDialog.setOriginUsers(row.users || []);
                                editCarDialog.onOpen();
                            }}
                        >
                            編輯
                        </Button>
                        <Button
                            variant='contained'
                            className={classes.buttonRed}
                            onClick={() =>
                                page.alert.show({
                                    type: 'warning',
                                    title: '警告',
                                    message: '確定要刪除？',
                                    onConfirm: async () => {
                                        page.setIsLoading(true);
                                        const ret = await api.car.remove({ carId: row.carId! }).finally(() => page.setIsLoading(false));
                                        if (ret.success) {
                                            reloadCars();
                                            page.simpleAlert.show({ autoHideDuration: 2000, severity: 'success', message: ret.message });
                                        } else {
                                            page.simpleAlert.show({
                                                autoHideDuration: 6000,
                                                severity: 'error',
                                                message: JSON.stringify(ret),
                                            });
                                        }
                                    },
                                })
                            }
                        >
                            刪除
                        </Button>
                    </Grid>
                );
            },
        },
    ];
    useEffect(() => {
        reloadCars();
    }, []);
    return {
        page,
        classes,
        auth,
        cars,
        setCars,
        mode,
        setMode,
        reloadCars,
        sideBar,
        editCarDialog,
        editUserDialog,
        inputSearchConfig,
        setInputSearchConfig,
        columns,
    };
};

export const CarManagePage = (): React.ReactElement => {
    const props = useCarManagePage();
    return (
        <Page {...props.page} title='車輛管理'>
            <Grid container flexDirection={'row'} height={'100%'}>
                <Grid container item flex={1} flexDirection={'column'} padding={4} sx={{ backgroundColor: grey[100] }}>
                    <Grid container item flexDirection={'row'}>
                        <Grid container item flexDirection={'row'} justifyContent={'flex-end'} xs={12}>
                            <TextField
                                value={props.inputSearchConfig}
                                onChange={(e) => props.setInputSearchConfig(e.target.value)}
                                variant='outlined'
                                label={
                                    <>
                                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                                        {`搜尋(${['車牌號碼', '使用者'].join(', ')})`}
                                    </>
                                }
                                style={{ flex: 1, width: '100%', margin: 4, marginTop: 12, backgroundColor: 'white' }}
                            />
                            <Button
                                className={props.classes.buttonCyan}
                                style={{ margin: 4, marginTop: 12 }}
                                onClick={() => {
                                    props.editCarDialog.setInputCarId('');
                                    props.editCarDialog.setEditMode('create');
                                    props.editCarDialog.setOriginUsers([]);
                                    props.editCarDialog.onOpen();
                                }}
                            >
                                <FontAwesomeIcon icon={faPlus} fixedWidth />
                                {'新增車輛'}
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid container item flex={1} padding={2} borderRadius={2} marginTop={2} sx={{ backgroundColor: 'white' }}>
                        <DataGrid
                            rows={(() => {
                                const searchs = props.inputSearchConfig.split(/\s+/).filter((v) => v.length > 0);
                                return props.cars.filter((v, i, a) =>
                                    [
                                        searchs.length == 0
                                            ? true
                                            : searchs
                                                  .map(
                                                      (s) =>
                                                          v.carId?.toUpperCase().includes(s.toUpperCase()) ||
                                                          (v.users || [])
                                                              .map((user) => user.userName || user.account!)
                                                              .join(', ')
                                                              .toUpperCase()
                                                              .includes(s.toUpperCase()),
                                                  )
                                                  .every((v) => v),
                                    ].every((v) => v),
                                );
                            })()}
                            columns={props.columns}
                            getRowId={(row: Car) => row.carId!}
                            pageSize={10}
                            rowsPerPageOptions={[10]}
                            experimentalFeatures={{ newEditingApi: true }}
                            disableColumnMenu={true}
                            disableSelectionOnClick={true}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <EditCarDialog {...props.editCarDialog} />
            <EditUserDialog {...props.editUserDialog} />
        </Page>
    );
};
